/**
 * Palettvalget for panelet (M2.6). Tre tilstander: `auto` følger operativsystemet,
 * `light` og `dark` overstyrer det.
 *
 * `localStorage` er BEVISST brukt her. K8 gjelder AUTENTISERING, og en fargepreferanse
 * er ikke en sesjon: den bærer ingen identitet, gir ingen tilgang, og en angriper som
 * leser den lærer hvilken palett noen liker. Det var argumentet da denne filen var det
 * ENESTE unntaket (M2.6).
 *
 * Den er det ikke lenger: `src/auth/supabase.ts` legger sesjonen i `localStorage` fra
 * M2.7, etter en egen beslutning i `tech-decisions.md` — og M2.7 advarte eksplisitt mot
 * å lese palettvalget som presedens for sesjonen. Advarselen står. To unntak er ikke en
 * åpning; de er to navngitte beslutninger.
 *
 * Vakt 1 i `eslint.config.js` forbyr `localStorage` i hele panelet med NØYAKTIG de to
 * unntakene. Listen er navngitt i regelen, ikke i en kommentar, og
 * `test/lint-vakter.test.ts` blir rød hvis et tredje glir inn — et forbud med et
 * udokumentert hull er verre enn ingen regel.
 *
 * Attributtet er `data-innspel-theme`, som `theme/generated/tokens.css` allerede
 * definerer (`:root` lys, `[data-innspel-theme='dark']` mørk). Ingen ny CSS, ingen
 * `prefers-color-scheme` i tokens-filen — den er delt med portalen, der paletten
 * velges av leietakeren og aldri av sluttbrukerens operativsystem.
 */
export type Temavalg = 'auto' | 'light' | 'dark';
export type Palett = 'light' | 'dark';

const NØKKEL = 'innspel:tema';
const MØRK = '(prefers-color-scheme: dark)';

const gyldig = (v: unknown): v is Temavalg => v === 'auto' || v === 'light' || v === 'dark';

/** Valget slik det er lagret. `auto` når ingenting er lagret, eller verdien er tull. */
export function lesValg(lagring: Pick<Storage, 'getItem'> = localStorage): Temavalg {
  try {
    const v = lagring.getItem(NØKKEL);
    return gyldig(v) ? v : 'auto';
  } catch {
    // Privat modus og blokkerte tredjepartsdata kaster ved lesing. `auto` er da riktig
    // svar, ikke en feil å vise brukeren.
    return 'auto';
  }
}

export function skrivValg(valg: Temavalg, lagring: Pick<Storage, 'setItem'> = localStorage): void {
  try {
    lagring.setItem(NØKKEL, valg);
  } catch {
    // Valget gjelder for denne fanen uansett; at det ikke overlever er en degradering,
    // ikke en feil.
  }
}

/** Hva `auto` faktisk blir på denne maskinen akkurat nå. */
export function systemPalett(vindu: Pick<Window, 'matchMedia'> = window): Palett {
  return vindu.matchMedia(MØRK).matches ? 'dark' : 'light';
}

export function løsPalett(valg: Temavalg, vindu: Pick<Window, 'matchMedia'> = window): Palett {
  return valg === 'auto' ? systemPalett(vindu) : valg;
}

/**
 * Setter attributtet på `<html>`. Lys palett er `:root` og skal IKKE ha attributtet —
 * settes det til `light`, treffer ingen selektor og panelet arver `:root` uansett;
 * men da lyver DOM-en om at noe er overstyrt. Attributtet fjernes derfor for lys.
 */
export function brukPalett(palett: Palett, rot: HTMLElement = document.documentElement): void {
  if (palett === 'dark') rot.setAttribute('data-innspel-theme', 'dark');
  else rot.removeAttribute('data-innspel-theme');
  rot.style.colorScheme = palett;
}

/**
 * Kobler valget til flaten, og holder `auto` i takt når operativsystemet skifter mens
 * panelet står åpent. Returnerer en oppryddingsfunksjon.
 */
export function start(valg: Temavalg, vindu: Window = window): () => void {
  brukPalett(løsPalett(valg, vindu));
  if (valg !== 'auto') return () => {};
  const mq = vindu.matchMedia(MØRK);
  const på = () => brukPalett(systemPalett(vindu));
  mq.addEventListener('change', på);
  return () => mq.removeEventListener('change', på);
}
